import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext'
import { useTheme } from '../context/shared/ThemeContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/shared/apiService';

interface Intervention {
  id: string;
  prompt: string;
  createdAt: string;
  status: 'pending' | 'engaged' | 'skipped' | 'dismissed';
  negativeTagCount: number;
}

const GuidedInterventionCard: React.FC = () => {
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch pending intervention
  const { data: intervention, isLoading } = useQuery({
    queryKey: ['pending-intervention', user?.uid],
    queryFn: () => apiService.makeRequest('/api/interventions/pending'),
    enabled: !!user?.uid,
  });

  // Action mutations
  const engageMutation = useMutation({
    mutationFn: (interventionId: string) =>
      apiService.makeRequest(`/api/interventions/engage/${interventionId}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-intervention', user?.uid] });
    },
  });

  const skipMutation = useMutation({
    mutationFn: (interventionId: string) =>
      apiService.makeRequest(`/api/interventions/skip/${interventionId}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-intervention', user?.uid] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (interventionId: string) =>
      apiService.makeRequest(`/api/interventions/dismiss/${interventionId}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-intervention', user?.uid] });
    },
  });

  const handleEngage = async () => {
    if (intervention?.id) {
      try {
        await engageMutation.mutateAsync(intervention.id);
        setIsExpanded(true);
      } catch (error) {
        console.error('Failed to engage intervention:', error);
      }
    }
  };

  const handleSkip = async () => {
    if (intervention?.id) {
      try {
        await skipMutation.mutateAsync(intervention.id);
      } catch (error) {
        console.error('Failed to skip intervention:', error);
      }
    }
  };

  const handleDismiss = async () => {
    if (intervention?.id) {
      try {
        await dismissMutation.mutateAsync(intervention.id);
      } catch (error) {
        console.error('Failed to dismiss intervention:', error);
      }
    }
  };

  // Don't show if no pending intervention
  if (!intervention || isLoading) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-6 ${themeClasses.cardBg}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
            <span className="text-blue-600 dark:text-blue-300 text-lg">💡</span>
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              You might find this helpful
            </h3>
            <button
              onClick={handleDismiss}
              disabled={dismissMutation.isPending}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Based on your recent patterns, here's a gentle prompt for reflection:
          </p>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-blue-100 dark:border-blue-800">
            <p className="text-gray-900 dark:text-white font-medium italic">
              "{intervention.prompt}"
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleEngage}
              disabled={engageMutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {engageMutation.isPending ? 'Opening...' : 'Reflect on This'}
            </button>
            
            <button
              onClick={handleSkip}
              disabled={skipMutation.isPending}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
            >
              {skipMutation.isPending ? 'Skipping...' : 'Skip'}
            </button>
          </div>
          
          {isExpanded && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                Take a moment to reflect on this question. You can:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• Write your thoughts in your journal</li>
                <li>• Discuss it with someone you trust</li>
                <li>• Simply sit with the question for a few minutes</li>
                <li>• Use it as a prompt for your next chat session</li>
              </ul>
            </div>
          )}
          
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            This suggestion was created based on patterns in your recent wellness data. 
            It's completely optional and designed to support your journey.
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedInterventionCard; 